// src/pages/Quests.tsx
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../i18n";
import { applyQuestTranslation, getPreferredQuestLocales, safeParseMeta } from "../lib/questI18n";
import { partnersList } from "../api/endpoints";
import { request } from "../api/http";
import { loadQuests, loadCategories, loadSubcategories } from "../offline/catalogCache";
import { syncCatalogIfChangedWeb } from "../offline/catalogSyncWeb";
import QuestContentCard from "../components/QuestContentCard";
import { enqueueOutbox } from "../offline/webOutbox";


type FilterKind = "all" | "base" | "adult";

type QuestRow = {
  id?: string | number;
  slug?: string | null;
  title?: string | null;
  body?: string | null;
  excerpt?: string | null;
  summary?: string | null;
  meta?: any;
  is_adult?: boolean;
  adult?: boolean;
  kind?: string | null;
  category?: string | null;
  category_key?: string | null;
  estimatedMinutes?: number | null;
  durationMinutes?: number | null;
  minutes?: number | null;
  est_duration?: string | null;
  modes?: string[] | null;
};

type QuestDraftReview = {
  status?: string | null;
  publicStatus?: string | null;
  requestedVisibility?: string | null;
  submittedAt?: string | null;
  changesRequestedAt?: string | null;
  changesRequestedReason?: string | null;
  rejectedAt?: string | null;
  rejectionReason?: string | null;
  approvedAt?: string | null;
  canEdit?: boolean;
  canSubmitReview?: boolean;
};

type QuestDraftRow = QuestRow & {
  review?: QuestDraftReview | null;
  status?: string | null;
};

function isAdultQuest(q: any): boolean {
  return q?.is_adult === true || q?.isAdult === true || q?.adult === true || q?.kind === "adult";
}

function questKey(q: any, idx: number): string {
  return String(q?.id || q?.slug || q?.code || q?.title || idx);
}

function questMinutes(q: any): string {
  const direct = q?.estimatedMinutes ?? q?.durationMinutes ?? q?.minutes ?? null;
  if (direct) return `${direct} min`;

  const txt = String(q?.est_duration || "").trim();
  return txt || "";
}

function parseItems(res: any): QuestRow[] {
  const items = res?.items ?? res?.rows ?? res?.data?.items ?? res?.data ?? [];
  return Array.isArray(items) ? items : [];
}


function reviewStatusLabel(status: any) {
  const s = String(status || "DRAFT").trim().toUpperCase();
  if (s === "PENDING") return "Pending review";
  if (s === "CHANGES_REQUESTED") return "Changes requested";
  if (s === "REJECTED") return "Rejected";
  if (s === "APPROVED") return "Approved";
  if (s === "PUBLISHED") return "Published";
  return "Draft";
}

function reviewStatusClass(status: any) {
  const s = String(status || "").trim().toUpperCase();
  if (s === "PENDING") return "border-amber-500/40 bg-amber-500/10 text-amber-200";
  if (s === "CHANGES_REQUESTED") return "border-sky-500/40 bg-sky-500/10 text-sky-200";
  if (s === "REJECTED") return "border-rose-500/40 bg-rose-500/10 text-rose-200";
  if (s === "APPROVED" || s === "PUBLISHED") return "border-emerald-500/40 bg-emerald-500/10 text-emerald-200";
  return "border-slate-700 bg-slate-900 text-slate-300";
}

function MyQuestSubmissions() {
  const [items, setItems] = useState<QuestDraftRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | number | null>(null);
  const [bodyEdits, setBodyEdits] = useState<Record<string, string>>({});

  async function loadDrafts() {
    setLoading(true);
    setError(null);
    try {
      const res = await request("/quests/drafts?status=ALL&limit=50", { auth: true });
      const next = Array.isArray(res?.items) ? res.items : [];
      setItems(next);
      setBodyEdits((prev) => {
        const out = { ...prev };
        for (const item of next) {
          const id = String(item?.id || "");
          if (id && out[id] == null) out[id] = String(item?.body || "");
        }
        return out;
      });
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.data?.error || err?.message || "Could not load your quest submissions.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadDrafts();
  }, []);

  async function saveDraft(item: QuestDraftRow) {
    const id = item.id;
    if (!id) return;

    setBusyId(id);
    setError(null);
    try {
      await request(`/quests/drafts/${encodeURIComponent(String(id))}`, {
        method: "PATCH",
        auth: true,
        body: {
          body: bodyEdits[String(id)] ?? item.body ?? "",
        },
      });
      await loadDrafts();
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.data?.error || err?.message || "Could not save draft.";
      setError(msg);
    } finally {
      setBusyId(null);
    }
  }

  async function submitReview(item: QuestDraftRow) {
    const id = item.id;
    if (!id) return;

    setBusyId(id);
    setError(null);
    try {
      await request(`/quests/${encodeURIComponent(String(id))}/submit-review`, {
        method: "POST",
        auth: true,
        body: { visibility: "public" },
      });
      await loadDrafts();
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.data?.error || err?.message || "Could not submit for review.";
      setError(msg);
    } finally {
      setBusyId(null);
    }
  }

  if (!loading && !error && items.length === 0) {
    return null;
  }

  return (
    <section className="rounded-3xl border border-slate-800 bg-slate-950/60 p-4 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-extrabold text-slate-50">My quest submissions</h2>
          <p className="text-xs text-slate-400">
            Track drafts, review status, changes requested by moderation, and resubmit after editing.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="rounded-full border border-slate-700 px-3 py-1.5 text-xs font-semibold text-slate-200 hover:bg-slate-900"
          >
            {open ? "Collapse" : "Open"}
          </button>
          <button
            type="button"
            onClick={() => loadDrafts()}
            disabled={loading}
            className="rounded-full border border-slate-700 px-3 py-1.5 text-xs font-semibold text-slate-200 hover:bg-slate-900 disabled:opacity-60"
          >
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-2xl border border-rose-500/40 bg-rose-500/10 p-3 text-xs text-rose-200">
          {error}
        </div>
      )}

      {open && (
        <div className="grid gap-3">
          {loading ? (
            <div className="text-sm text-slate-400">Loading submissions…</div>
          ) : items.length === 0 ? (
            <div className="text-sm text-slate-400">No quest submissions yet.</div>
          ) : (
            items.map((item) => {
              const id = String(item.id || "");
              const review = item.review || {};
              const status = review.status || item.meta?.moderation_status || item.status || "DRAFT";
              const canEdit = review.canEdit === true;
              const canSubmit = review.canSubmitReview === true;
              const reason =
                review.changesRequestedReason ||
                review.rejectionReason ||
                item.meta?.changes_requested_reason ||
                item.meta?.rejection_reason ||
                "";

              return (
                <article key={id || String(item.title)} className="rounded-2xl border border-slate-800 bg-slate-950/70 p-3 space-y-3">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-sm font-bold text-slate-50">
                          {item.title || `Quest #${id}`}
                        </h3>
                        <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${reviewStatusClass(status)}`}>
                          {reviewStatusLabel(status)}
                        </span>
                      </div>
                      <p className="mt-1 text-[11px] text-slate-500">
                        #{id} · {item.category || item.category_key || "No category"} · {item.intensity || "No intensity"}
                      </p>
                    </div>

                    <Link
                      to={`/quests/${encodeURIComponent(id)}`}
                      className="rounded-full border border-slate-700 px-3 py-1.5 text-xs font-semibold text-slate-200 hover:bg-slate-900"
                    >
                      Open
                    </Link>
                  </div>

                  {reason && (
                    <div className="rounded-2xl border border-sky-500/30 bg-sky-500/10 p-3 text-xs text-sky-100">
                      <div className="font-semibold">Moderator note</div>
                      <div className="mt-1 whitespace-pre-wrap">{reason}</div>
                    </div>
                  )}

                  {canEdit ? (
                    <textarea
                      value={bodyEdits[id] ?? String(item.body || "")}
                      onChange={(e) =>
                        setBodyEdits((prev) => ({ ...prev, [id]: e.target.value }))
                      }
                      className="min-h-28 w-full rounded-2xl border border-slate-700 bg-slate-950 p-3 text-sm text-slate-100 outline-none focus:border-sky-500"
                    />
                  ) : (
                    <p className="whitespace-pre-wrap rounded-2xl border border-slate-800 bg-slate-950 p-3 text-sm text-slate-300">
                      {item.body || item.summary || item.excerpt || "No description."}
                    </p>
                  )}

                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      disabled={!canEdit || busyId === item.id}
                      onClick={() => saveDraft(item)}
                      className="rounded-full bg-sky-600 px-3 py-1.5 text-xs font-bold text-white disabled:opacity-50"
                    >
                      Save edits
                    </button>
                    <button
                      type="button"
                      disabled={!canSubmit || busyId === item.id}
                      onClick={() => submitReview(item)}
                      className="rounded-full bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white disabled:opacity-50"
                    >
                      Resubmit for review
                    </button>
                    {!canEdit && (
                      <span className="text-xs text-slate-500">
                        Editing is locked while pending review or after approval.
                      </span>
                    )}
                  </div>
                </article>
              );
            })
          )}
        </div>
      )}
    </section>
  );
}


const QuestsPage: React.FC = () => {
  function queueQuestPick(q: any) {
    try {
      void enqueueOutbox("quest_pick", {
        questId: String(q?.id || q?.slug || ""),
        pickedAt: Date.now(),
      });
    } catch {}
  }

  const { t } = useI18n();
  const [filterKind, setFilterKind] = useState<FilterKind>("all");
  const [quests, setQuests] = useState<QuestRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeLinkId, setActiveLinkId] = useState<string | null>(null);

  const preferredLocales = useMemo(() => getPreferredQuestLocales(), []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const cachedQuests = await loadQuests().catch(() => null);
        if (cachedQuests?.items?.length) {
          setQuests(cachedQuests.items);
        }

        const p = await partnersList().catch(() => ({ links: [] }));
        const links = Array.isArray(p?.links) ? p.links : [];
        const accepted = links.find((x: any) => String(x?.status || "").toUpperCase() === "ACCEPTED");
        const linkId = accepted?.id ? String(accepted.id) : null;
        setActiveLinkId(linkId);

        try {
          await syncCatalogIfChangedWeb();
        } catch {}

        const freshCached = await loadQuests().catch(() => null);
        if (freshCached?.items?.length) {
          setQuests(freshCached.items);
          setError(null);
          return;
        }

        const res = await request("/quests", { auth: true });
        const items = parseItems(res);

        setQuests(items);
        setError(null);
      } catch (err: any) {
        setError(err?.response?.data?.error || err?.message || "Could not load quests.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const localizedQuests = useMemo(() => {
    return quests.map((q, idx) => {
      const localized = applyQuestTranslation(
        {
          title: String(q?.title || `Quest ${idx + 1}`),
          body: String(q?.body || ""),
          excerpt: String(q?.excerpt || ""),
          summary: String(q?.summary || ""),
        },
        safeParseMeta(q?.meta),
        preferredLocales
      );

      return {
        ...q,
        title: localized.title || q.title,
        body: localized.body || q.body,
        excerpt: localized.excerpt || q.excerpt,
        summary: localized.summary || q.summary,
      };
    });
  }, [quests, preferredLocales]);

  const [catalogMeta, setCatalogMeta] = useState<{ categories: number; subcategories: number }>({
    categories: 0,
    subcategories: 0,
  });

  useEffect(() => {
    (async () => {
      const [cats, subs] = await Promise.all([
        loadCategories().catch(() => null),
        loadSubcategories().catch(() => null),
      ]);
      setCatalogMeta({
        categories: cats?.items?.length || 0,
        subcategories: subs?.items?.length || 0,
      });
    })();
  }, []);

  const filtered = useMemo(
    () =>
      localizedQuests.filter((q: any) =>
        filterKind === "all"
          ? true
          : filterKind === "adult"
          ? isAdultQuest(q)
          : !isAdultQuest(q),
      ),
    [localizedQuests, filterKind],
  );

  const debugRows = useMemo(() => {
    return localizedQuests.slice(0, 5).map((q: any, idx: number) => ({
      key: questKey(q, idx),
      id: q?.id,
      slug: q?.slug,
      title: q?.title,
      isAdult: isAdultQuest(q),
      kind: q?.kind,
      category: q?.category || q?.category_key,
    }));
  }, [localizedQuests]);

  if (loading) {
    return <div className="center-page">{t("common.loading")}</div>;
  }

  if (error) {
    return <div className="center-page">{error}</div>;
  }

  return (
    <div className="space-y-6">
      <header className="rounded-3xl border border-slate-800 bg-slate-950/70 p-5 space-y-2">
        <h1 className="text-2xl font-extrabold text-slate-50">
          {t("quests.title")}
        </h1>
        <p className="text-sm text-slate-400 max-w-2xl">
          {t("quests.subtitle")}
        </p>
        {!!activeLinkId && (
          <p className="text-xs text-slate-500">
            Partner link active: {activeLinkId}
          </p>
        )}
      </header>

      <MyQuestSubmissions />

      <div className="rounded-2xl border border-slate-800 bg-slate-950/50 p-4 text-xs text-slate-400 space-y-2">
        <div className="flex flex-wrap gap-4">
          <span>
            raw quests: <span className="font-semibold text-slate-200">{quests.length}</span>
          </span>
          <span>
            cached categories: <span className="font-semibold text-slate-200">{catalogMeta.categories}</span>
          </span>
          <span>
            cached subcategories: <span className="font-semibold text-slate-200">{catalogMeta.subcategories}</span>
          </span>
          <span>
            localized: <span className="font-semibold text-slate-200">{localizedQuests.length}</span>
          </span>
          <span>
            filtered: <span className="font-semibold text-slate-200">{filtered.length}</span>
          </span>
          <span>
            filter: <span className="font-semibold text-slate-200">{filterKind}</span>
          </span>
        </div>

        {debugRows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full text-[11px]">
              <thead>
                <tr className="text-slate-500">
                  <th className="px-2 py-1 text-left">id</th>
                  <th className="px-2 py-1 text-left">slug</th>
                  <th className="px-2 py-1 text-left">title</th>
                  <th className="px-2 py-1 text-left">adult</th>
                  <th className="px-2 py-1 text-left">kind</th>
                  <th className="px-2 py-1 text-left">category</th>
                </tr>
              </thead>
              <tbody>
                {debugRows.map((r: any) => (
                  <tr key={String(r.key)} className="border-t border-slate-800 text-slate-300">
                    <td className="px-2 py-1">{String(r.id ?? "")}</td>
                    <td className="px-2 py-1">{String(r.slug ?? "")}</td>
                    <td className="px-2 py-1">{String(r.title ?? "")}</td>
                    <td className="px-2 py-1">{r.isAdult ? "yes" : "no"}</td>
                    <td className="px-2 py-1">{String(r.kind ?? "")}</td>
                    <td className="px-2 py-1">{String(r.category ?? "")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2 rounded-2xl border border-slate-800 bg-slate-950/50 p-3 text-xs">
        <button
          type="button"
          onClick={() => setFilterKind("all")}
          className={
            "px-3 py-1.5 rounded-full border transition-colors " +
            (filterKind === "all"
              ? "bg-pink-500 text-white border-pink-500"
              : "border-slate-700 text-slate-300 hover:border-slate-500")
          }
        >
          {t("quests.filters.all")}
        </button>
        <button
          type="button"
          onClick={() => setFilterKind("base")}
          className={
            "px-3 py-1.5 rounded-full border transition-colors " +
            (filterKind === "base"
              ? "bg-pink-500 text-white border-pink-500"
              : "border-slate-700 text-slate-300 hover:border-slate-500")
          }
        >
          {t("quests.filters.base")}
        </button>
        <button
          type="button"
          onClick={() => setFilterKind("adult")}
          className={
            "px-3 py-1.5 rounded-full border transition-colors " +
            (filterKind === "adult"
              ? "bg-pink-500 text-white border-pink-500"
              : "border-slate-700 text-slate-300 hover:border-slate-500")
          }
        >
          {t("quests.filters.adult")}
        </button>
      </div>

      {!filtered.length ? (
        <div className="rounded-3xl border border-slate-800 bg-slate-950/70 p-5 text-sm text-slate-400 space-y-2">
          <div>No quests available for this filter yet.</div>
          <div className="text-xs text-slate-500">
            raw={quests.length} · localized={localizedQuests.length} · filtered={filtered.length} · filter={filterKind}
          </div>
        </div>
      ) : (
        <div className="grid gap-4 xl:grid-cols-2">
          {filtered.map((q: any, idx: number) => (
            <div key={questKey(q, idx)} className="space-y-3">
              <QuestContentCard
                quest={{
                  ...q,
                  summary: q.summary || q.excerpt,
                  is_adult: isAdultQuest(q),
                }}
                language={preferredLocales[0] || "en"}
              />

              <div className="flex items-center justify-between px-1">
                <div className="text-xs text-slate-500">
                  {questMinutes(q)}
                </div>
                <Link
                  to={`/quests/${encodeURIComponent(String(q?.id || q?.slug || ""))}`}
                  onClick={() => queueQuestPick(q)}
                  className="inline-flex items-center gap-1 rounded-full bg-pink-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-pink-400"
                >
                  {t("quests.playButton")}
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default QuestsPage;
